/**
 * Analytics utility for pushing events to GTM dataLayer
 *
 * Usage:
 * import { trackEvent } from '@/utilities/analytics'
 * trackEvent('application_form_open', { position: 'drone-pilot' })
 */

// Extend Window interface for dataLayer
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[]
  }
}

export type AnalyticsEventName =
  // Conversions
  | 'application_form_open'
  | 'application_civil_submit'
  | 'application_military_submit'
  | 'application_form_error'
  // Engagement
  | 'vacancy_view'
  | 'vacancy_apply_click'
  | 'faq_expand'
  | 'social_link_click'
  | 'contact_email_click'
  | 'contact_phone_click'
  | 'language_switch'
  // Navigation
  | 'scroll_depth'
  | 'cta_hero_click'
  | 'nav_item_click'
  | 'outbound_link_click'

export interface AnalyticsEventParams {
  // Application events
  position?: string
  form_type?: 'civil' | 'military'
  error_message?: string
  // Vacancy events
  vacancy_title?: string
  vacancy_slug?: string
  // Social events
  platform?: string
  url?: string
  // FAQ events
  question?: string
  // Language events
  from_language?: string
  to_language?: string
  // Navigation events
  link_label?: string
  link_url?: string
  // Scroll events
  percent?: number
  // Generic
  [key: string]: unknown
}

/**
 * Push an event to GTM dataLayer
 */
export function trackEvent(
  eventName: AnalyticsEventName,
  params?: AnalyticsEventParams,
): void {
  if (typeof window === 'undefined') return

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: eventName,
    ...params,
  })
}

/**
 * Track application form open
 */
export function trackApplicationFormOpen(position?: string): void {
  trackEvent('application_form_open', { position })
}

/**
 * Track civil application submit
 */
export function trackCivilApplicationSubmit(position?: string): void {
  trackEvent('application_civil_submit', { position, form_type: 'civil' })
}

/**
 * Track military application submit
 */
export function trackMilitaryApplicationSubmit(position?: string): void {
  trackEvent('application_military_submit', { position, form_type: 'military' })
}

/**
 * Track application form error
 */
export function trackApplicationFormError(errorMessage: string, formType: 'civil' | 'military'): void {
  trackEvent('application_form_error', { error_message: errorMessage, form_type: formType })
}

/**
 * Track vacancy view
 */
export function trackVacancyView(title: string, slug: string): void {
  trackEvent('vacancy_view', { vacancy_title: title, vacancy_slug: slug })
}

/**
 * Track vacancy apply click
 */
export function trackVacancyApplyClick(title: string, slug: string): void {
  trackEvent('vacancy_apply_click', { vacancy_title: title, vacancy_slug: slug })
}

/**
 * Track FAQ expand
 */
export function trackFaqExpand(question: string): void {
  trackEvent('faq_expand', { question })
}

/**
 * Track social link click
 */
export function trackSocialLinkClick(platform: string, url: string): void {
  trackEvent('social_link_click', { platform, url })
}

/**
 * Track contact email click
 */
export function trackContactEmailClick(email: string): void {
  trackEvent('contact_email_click', { url: `mailto:${email}` })
}

/**
 * Track contact phone click
 */
export function trackContactPhoneClick(phone: string): void {
  trackEvent('contact_phone_click', { url: `tel:${phone}` })
}

/**
 * Track language switch
 */
export function trackLanguageSwitch(fromLanguage: string, toLanguage: string): void {
  trackEvent('language_switch', { from_language: fromLanguage, to_language: toLanguage })
}

/**
 * Track hero CTA click
 */
export function trackHeroCtaClick(label: string, url: string): void {
  trackEvent('cta_hero_click', { link_label: label, link_url: url })
}

/**
 * Track nav item click
 */
export function trackNavItemClick(label: string, url: string): void {
  trackEvent('nav_item_click', { link_label: label, link_url: url })
}

